/**
 * Portal Manta · Card4Similaridade
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import { M, F, STATUS } from '../../tokens/theme.js';
import { fmtNum } from '../../globals.js';

const Card4Similaridade = memo(function Card4Similaridade_Inner({qual, kpis, filtros, escopoSugerido, escopo, setEscopo, pesoFis, setPesoFis, similaridades, casosFiltrados, loteEscolhido, setLoteEscolhido}){
  const ufP = qual.uf;
  const regiaoP = UF_REGIAO[ufP] || 'Sul';
  const escopoLabel = {estado:'Mesma UF ('+ufP+')', regiao:'Mesma região ('+regiaoP+')', proximos:'Estados próximos', todos:'Todos os casos'};
  const corMatch = (sig) => ({'3':M.green, '4':M.blue, '5':M.accent})[sig] || '#7C3AED';
  const sugerido = similaridades[0]?.sig;
  const escolhido = loteEscolhido || sugerido;

  return (<div>
    {/* Controles · escopo + tuning */}
    <div style={{display:'grid', gridTemplateColumns:'2fr 1fr', gap:14, marginBottom:18}}>
      {/* Escopo */}
      <div style={{padding:'12px 14px', background:M.bgAlt, borderRadius:6, border:`1px solid ${M.borderSoft}`}}>
        <div style={{fontSize:10, fontWeight:700, color:M.muted, letterSpacing:.5, textTransform:'uppercase', marginBottom:8}}>Escopo da busca</div>
        <div style={{display:'flex', gap:6, flexWrap:'wrap'}}>
          {['estado','regiao','proximos','todos'].map(k => {
            const ativo = (escopo||escopoSugerido) === k;
            const n = filtros[k].length;
            const sugerido_aqui = k === escopoSugerido;
            return (<button key={k} onClick={()=>setEscopo(k)} disabled={n===0} style={{
              padding:'6px 12px', fontSize:10.5, fontWeight:700, borderRadius:99, cursor:n===0?'default':'pointer',
              border:`1.5px solid ${ativo?M.accent:M.border}`,
              background: ativo?M.accent:'#fff', color: ativo?'#fff':(n===0?M.light:M.text),
              fontFamily:F.body, opacity:n===0?0.5:1, position:'relative',
              display:'inline-flex', alignItems:'center', gap:5,
            }}>
              {escopoLabel[k]} <span style={{fontFamily:F.mono, fontSize:9, opacity:0.8}}>({n})</span>
              {sugerido_aqui && <span style={{fontSize:9, fontWeight:700, color:ativo?'#fff':M.green}}>★</span>}
            </button>);
          })}
        </div>
      </div>

      {/* Tuning peso */}
      <div style={{padding:'12px 14px', background:M.bgAlt, borderRadius:6, border:`1px solid ${M.borderSoft}`}}>
        <div style={{fontSize:10, fontWeight:700, color:M.muted, letterSpacing:.5, textTransform:'uppercase', marginBottom:8}}>Peso · físico × contexto</div>
        <div style={{display:'flex', justifyContent:'space-between', fontSize:10, color:M.text, fontFamily:F.mono, marginBottom:4}}>
          <span>Físico {(pesoFis*100).toFixed(0)}%</span>
          <span>Contexto {((1-pesoFis)*100).toFixed(0)}%</span>
        </div>
        <input type="range" min="0" max="1" step="0.05" value={pesoFis} onChange={e=>setPesoFis(parseFloat(e.target.value))} style={{width:'100%'}}/>
      </div>
    </div>

    {/* Ranking Top 5 */}
    {similaridades.length === 0 ? (
      <div style={{padding:24, textAlign:'center', color:M.muted, fontSize:12, fontStyle:'italic', background:M.bgAlt, borderRadius:6}}>
        Nenhum caso disponível neste escopo. Amplie a busca acima.
      </div>
    ) : (<div>
      <div style={{fontSize:10, fontWeight:700, color:M.muted, letterSpacing:.5, textTransform:'uppercase', marginBottom:10}}>
        Top {Math.min(5, similaridades.length)} mais similares
      </div>
      <div style={{display:'flex', flexDirection:'column', gap:8}}>
        {similaridades.slice(0, 5).map((s, i) => {
          const ativo = escolhido === s.sig;
          return (<div key={s.sig} onClick={()=>setLoteEscolhido(s.sig)} style={{
            padding:'14px 16px', borderRadius:6, cursor:'pointer',
            border:`2px solid ${ativo?M.accent:M.border}`,
            background:ativo?'#FFF7ED':'#fff', transition:'all .15s',
          }}>
            <div style={{display:'flex', alignItems:'center', gap:14}}>
              {/* Rank */}
              <div style={{
                width:32, height:32, borderRadius:'50%',
                background: ativo?M.accent:M.bgAlt,
                color: ativo?'#fff':M.muted,
                display:'flex', alignItems:'center', justifyContent:'center',
                fontFamily:F.display, fontSize:14, fontWeight:700, flexShrink:0,
              }}>{i+1}</div>

              {/* Nome */}
              <div style={{flex:1, minWidth:0}}>
                <div style={{display:'flex', alignItems:'baseline', gap:8, flexWrap:'wrap'}}>
                  <span style={{display:'inline-block', width:8, height:8, borderRadius:'50%', background:corMatch(s.lote.sig)}}/>
                  <b style={{color:corMatch(s.lote.sig), fontFamily:F.display, fontSize:14}}>{s.lote.sigla}</b>
                  <span style={{fontSize:11.5, color:M.text, fontWeight:600}}>{s.lote.nome.replace(/^Paraná · /,'')}</span>
                  {i===0 && <span style={{fontSize:9, fontWeight:700, color:'#fff', background:M.green, padding:'2px 7px', borderRadius:99, letterSpacing:.5}}>★ TOP</span>}
                </div>
                <div style={{fontSize:10, color:M.muted, marginTop:3}}>
                  {s.lote.uf} · {s.lote.cidade || 'sem localização'} · {fmtNum(s.lote.ext,1)} km · {s.lote.oaeQt} OAEs · chuva {s.lote.chuvaMM}mm/ano
                </div>
                {/* Drivers */}
                {s.drivers.length > 0 && (<div style={{display:'flex', gap:4, flexWrap:'wrap', marginTop:6}}>
                  {s.drivers.map((d,j) => (
                    <span key={j} style={{fontSize:9, padding:'1px 7px', background:M.bgAlt, color:M.muted, borderRadius:99, fontFamily:F.body}}>· {d}</span>
                  ))}
                </div>)}
              </div>

              {/* Score */}
              <div style={{textAlign:'right', flexShrink:0}}>
                <div style={{fontSize:22, fontWeight:800, fontFamily:F.mono, color:s.score>=80?M.green:(s.score>=60?M.blue:M.accent)}}>
                  {s.score}%
                </div>
                <div style={{fontSize:9, color:M.muted, letterSpacing:.5, textTransform:'uppercase', fontFamily:F.body}}>similaridade</div>
              </div>

              {/* Check */}
              <div style={{
                width:22, height:22, borderRadius:'50%',
                border:`2px solid ${ativo?M.accent:M.border}`,
                background:ativo?M.accent:'#fff',
                display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
              }}>
                {ativo && <span style={{color:'#fff', fontSize:11, fontWeight:800}}>✓</span>}
              </div>
            </div>

            {/* Barra visual */}
            <div style={{height:4, background:M.borderSoft, borderRadius:2, marginTop:10, overflow:'hidden'}}>
              <div style={{height:'100%', width:s.score+'%', background:corMatch(s.lote.sig), transition:'width .3s'}}/>
            </div>
          </div>);
        })}
      </div>
    </div>)}

    {/* Aviso se escolheu diferente */}
    {escolhido && escolhido !== sugerido && similaridades.find(s=>s.sig===escolhido) && (
      <div style={{marginTop:14, padding:'10px 14px', background:'#FEF3C7', borderLeft:`3px solid ${M.yellow}`, fontSize:11, color:'#78350f'}}>
        ⚠ Você escolheu um caso diferente do sugerido. Top: <b>{similaridades.find(s=>s.sig===sugerido)?.lote.sigla}</b> ({similaridades.find(s=>s.sig===sugerido)?.score}%). Escolhido: <b>{similaridades.find(s=>s.sig===escolhido)?.lote.sigla}</b> ({similaridades.find(s=>s.sig===escolhido)?.score}%). Confirme se faz sentido técnico.
      </div>
    )}
  </div>);
});

export { Card4Similaridade };
export default Card4Similaridade;
